import { useRedirectStore } from '@/stores/redirect';
import { useMatchRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

/**
 * usePostLoginRedirect
 *
 * Handles post-login redirect logic using app store state.
 * Navigates to the redirect path if set, then clears it.
 *
 * Usage:
 *   Call this hook in a component where post-login redirect should occur.
 *
 * Context7 Best Practices:
 * - Clear naming and documentation
 * - Composable and side-effect aware
 */
export function usePostLoginRedirect() {
  const { redirectPath, setRedirectPath } = useRedirectStore(
    useShallow(state => ({
      redirectPath: state.redirectPath,
      setRedirectPath: state.setRedirectPath,
    })),
  );
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  useEffect(() => {
    if (redirectPath) {
      // Robust validation: Only allow relative paths starting with "/" and no scheme/host,
      // and ensure the path matches a valid route in the router config.
      const isSafe =
        typeof redirectPath === 'string' &&
        redirectPath.startsWith('/') &&
        !/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(redirectPath) && // no scheme
        !redirectPath.startsWith('//'); // no protocol-relative

      const matchesRoute = !!matchRoute({ to: redirectPath });

      if (isSafe && matchesRoute) {
        navigate({ to: redirectPath, replace: true });
      }
      setRedirectPath(null);
    }
  }, [redirectPath, matchRoute, navigate, setRedirectPath]);
}
